import { useEffect, useState } from "react";
import {
  FieldLayout,
  PageTitleWithOutSideBarLayout,
  WithOutSideBarLayout,
} from "common/components";
import { VerticalGap } from "common/const/gaps";
import api from "common/utils/api";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { showMessageModal } from "common/utils/modalController";
import {
  isBusinessOwner,
  hasPermissions,
  isAdminOwner,
} from "common/store/userSession";
import { Row } from "common/components/Layout/new/constLayout";
import { DefaultButton } from "common/components/Button/new/DefaultButton";
import { shortButtonStyle } from "common/const/buttonStyles";
import { I18N_KEYS } from "common/i18n/keys";
import { FormProvider, useForm } from "react-hook-form";
import { TextInput } from "common/components/Input/TextInput";
import { CustomPhoneInput } from "common/components/Input/CustomPhoneInput";

const MyProfileScreen = () => {
  const hasPermissionsUpdate = hasPermissions("ACCOUNT", "isUpdate");
  const isAdmin = isAdminOwner();
  const isBusiness = isBusinessOwner();
  const location = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(location.state?.success ?? false);

  // 폼 외 데이터(제출 시 그대로 다시 보내는 값)
  const [editData, setEditData] = useState({});

  const methods = useForm({ mode: "onBlur" });

  useEffect(() => {
    api
      .get("/api/v1.0/users")
      .then((res) => {
        const data = res.data.result;
        const address = `${data.company.companyAddress.city} ${data.company.companyAddress.state} ${data.company.companyAddress.address1} ${data.company.companyAddress.address2}`;

        // 폼 값 채우기
        methods.reset({
          firstName: data.userName.firstName,
          lastName: data.userName.lastName,
          email: data.email,
          company: data.company.companyName,
          phoneCode: data.company.companyTel.phoneCode,
          phoneNumber: data.company.companyTel.phoneNumber,
          address,
          password: "",
          confirmPassword: "",
        });

        // 제출 시 다시 보낼 비폼 데이터
        setEditData({
          companyTel: data.company.companyTel,
          companyAddress: data.company.companyAddress,
          country: data.country,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const handleSave = methods.handleSubmit((values) => {
    api
      .patch("/api/v1.0/users", {
        newPw: values.password,
        newCountry: editData?.country,
        newCompanyName: values.company,
        newCompanyTel: editData?.companyTel,
        newCompanyAddress: editData?.companyAddress,
      })
      .then(() => {
        setIsEdit(false);
        methods.setValue("password", "");
        methods.setValue("confirmPassword", "");
      })
      .catch((error) => {
        console.log(error);
        if (
          error.response?.message === "Forbidden" ||
          error.response?.status === 403
        ) {
          showMessageModal(error.response.data.message);
        }
      });
  });

  const readOnlyProps = { readOnly: !isEdit, disabled: isEdit };

  return (
    <WithOutSideBarLayout>
      <FormProvider {...methods}>
        <PageTitleWithOutSideBarLayout
          title={t(I18N_KEYS.header.profile.title)}
          button={
            !isEdit && (
              <Row $align={"flex-end"}>
                <DefaultButton.Blue
                  style={shortButtonStyle}
                  text={t(I18N_KEYS.header.profile.button.edit)}
                  onClick={() => {
                    navigate("/verify-password", {
                      state: { nextPage: "/profile" },
                    });
                  }}
                  disabled={!isBusiness && !isAdmin && !hasPermissionsUpdate}
                />
              </Row>
            )
          }>
          <FieldLayout>
            <Row>
              <TextInput
                name={"firstName"}
                label={t(I18N_KEYS.header.profile.form.firstName)}
                placeholder={t(I18N_KEYS.header.profile.form.firstName)}
                {...readOnlyProps}
              />
              <TextInput
                name={"lastName"}
                label={t(I18N_KEYS.header.profile.form.lastName)}
                placeholder={t(I18N_KEYS.header.profile.form.lastName)}
                {...readOnlyProps}
              />
            </Row>

            <TextInput
              name={"email"}
              label={t(I18N_KEYS.header.profile.form.email)}
              placeholder={t(I18N_KEYS.header.profile.form.email)}
              {...readOnlyProps}
            />

            {isEdit && (
              <FieldLayout>
                <TextInput
                  name={"password"}
                  type={"password"}
                  label={t(I18N_KEYS.header.profile.form.password)}
                  placeholder={t(I18N_KEYS.header.profile.form.password)}
                  rules={{
                    required: true,
                    minLength: { value: 8 },
                  }}
                />
                <TextInput
                  name={"confirmPassword"}
                  type={"password"}
                  label={t(I18N_KEYS.header.profile.form.passwordConfirm)}
                  placeholder={t(I18N_KEYS.header.profile.form.passwordConfirm)}
                  rules={{
                    required: true,
                    validate: (value) =>
                      value === methods.getValues("password") ||
                      t(I18N_KEYS.header.profile.form.passwordConfirm),
                  }}
                />
              </FieldLayout>
            )}

            <TextInput
              name={"company"}
              label={t(I18N_KEYS.header.profile.form.companyName)}
              placeholder={t(I18N_KEYS.header.profile.form.companyName)}
              {...readOnlyProps}
            />
            <CustomPhoneInput
              codeFieldName={"phoneCode"}
              numberFieldName={"phoneNumber"}
              label={t(I18N_KEYS.header.profile.form.phoneNumber)}
              placeholder={t(I18N_KEYS.header.profile.form.phoneNumber)}
              disabled
            />
            <TextInput
              name={"address"}
              label={t(I18N_KEYS.header.profile.form.address)}
              placeholder={t(I18N_KEYS.header.profile.form.address)}
              {...readOnlyProps}
            />
          </FieldLayout>

          {isEdit && (
            <Row $justify={"flex-end"}>
              <DefaultButton.Blue
                style={shortButtonStyle}
                text={t(I18N_KEYS.header.profile.form.submit)}
                disabled={!methods.formState.isValid}
                onClick={handleSave}
              />
              <DefaultButton.White
                style={shortButtonStyle}
                text={t(I18N_KEYS.header.profile.form.cancel)}
                onClick={() => setIsEdit(false)}
              />
            </Row>
          )}

          <VerticalGap height="2rem" />
        </PageTitleWithOutSideBarLayout>
      </FormProvider>
    </WithOutSideBarLayout>
  );
};

export default MyProfileScreen;
